import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import Cookies from 'universal-cookie'
import { ActionError } from '../exceptions'
import { handleActionErrorGlobal } from '../composables/useErrorHandler'
import {
  getMiddleErrorMessage,
  isMiddleErrorResponse,
} from '../utils/middleErrorResponse'
import {
  isWorkflowPerfEnabled,
  profilePayload,
  startWorkflowMeasure,
  type PayloadProfile,
} from '@/common/utils/performanceTrace'
import { skipNextPageLeaveConfirm } from '@/common/utils/pageLeaveGuardBridge'

const API_BASE_URL = import.meta.env.DEV
  ? '/api'
  : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:1080/workflow-web-services/api')


export const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true // Required for CSRF token, JSESSIONID, and X-AUTH-TOKEN cookies
})

const AUTH_ENDPOINTS = [
  '/user/signin',
  '/user/signup',
  '/user/signout',
  '/user/refresh-token'
]

const cookies = new Cookies()

// Расширяем конфиг для кастомных свойств
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _isAuthRequest?: boolean
  /** Fail-silent: no session-expired redirect / connection retry side effects. */
  _skipGlobalErrorHandling?: boolean
  _workflowPerfEnd?: () => number
  _workflowRequestProfile?: PayloadProfile
}

/**
 * Keywords that indicate session expiration or authentication issues
 */
const SESSION_EXPIRATION_INDICATORS = [
  'session',
  'token',
  'auth',
  'illegalstateexception',
  'remembermeauthenticationtoken',
  'restauthentication',
  'current user principal',
  'principal is not of type',
  'authenticationtoken',
  'unauthorized',
  'expired',
  'signature',
  'invalid auth token'
] as const

/**
 * Exception types that indicate authentication/session issues
 */
const AUTH_EXCEPTION_PATTERNS = [
  /IllegalStateException/i,
  /RememberMeAuthenticationToken/i,
  /RESTAuthentication/i,
  /AuthenticationException/i,
  /SessionExpiredException/i
] as const

function containsSessionIndicator(text: string): boolean {
  const lowerText = text.toLowerCase()
  return SESSION_EXPIRATION_INDICATORS.some(indicator => lowerText.includes(indicator)) ||
    AUTH_EXCEPTION_PATTERNS.some(pattern => pattern.test(text))
}

function hasSessionExpirationIndicator(data: any): boolean {
  if (!data) return false

  const fieldsToCheck = [
    data.message,
    data.error,
    data.developerMessage,
    data.exception,
    data.errorType,
    data.status
  ]

  for (const field of fieldsToCheck) {
    if (!field) continue

    if (typeof field === 'string') {
      if (containsSessionIndicator(field)) return true
    } else if (typeof field === 'object') {
      if (containsSessionIndicator(JSON.stringify(field))) return true
    }
  }

  return false
}

/**
 * Проверяет, является ли ошибка признаком истекшей сессии
 */
function isSessionExpiredError(error: any): boolean {
  if (!error.response) return false

  const { status, data, headers } = error.response
  const url = error.config?.url || ''

  // Auth endpoints (signin, signup) никогда не являются признаком истекшей сессии
  if (AUTH_ENDPOINTS.some(endpoint => url.includes(endpoint))) {
    return false
  }

  // 4003 = MIDDLE_INVALID_AUTHENTICATION_TOKEN_SIGNATURE
  // 4000 = MIDDLE_SESSION_EXPIRED
  // 4002 = MIDDLE_INVALID_AUTHENTICATION_TOKEN_FORMAT
  if (data?.code === 4003 || data?.code === 4000 || data?.code === 4002) {
    return true
  }

  // 401 - неавторизован
  if (status === 401) {
    return true
  }

  if (status === 500) {
    const errorText = JSON.stringify(data || {}).toLowerCase()
    if (errorText.includes('invalid auth token') ||
      errorText.includes('auth token signature') ||
      errorText.includes('session expired') ||
      errorText.includes('authentication') && (errorText.includes('token') || errorText.includes('signature'))) {
      return true
    }
  }

  // 403 может быть CSRF ошибкой, не всегда означает истекшую сессию
  if (status === 403) {
    const isCsrfError = data?.message?.includes('CSRF') ||
      data?.error?.includes('CSRF') ||
      headers?.['x-csrf-error'] === 'true' ||
      headers?.['X-CSRF-Error'] === 'true'
    if (isCsrfError) {
      return false
    }
    return true
  }

  // 400 с определенными признаками
  if (status === 400) {
    if (url.includes('/execute-procedure')) {
      return false
    }

    const sessionExpiredHeader = headers?.['x-session-expired'] ?? headers?.['X-Session-Expired']
    if (sessionExpiredHeader === 'true') {
      return true
    }

    return hasSessionExpirationIndicator(data)
  }

  return false
}

/**
 * Проверяет, есть ли у пользователя полная сессия (JSESSIONID cookie).
 * WebSocket требует полную сессию, а не только remember-me токен.
 */
export function hasFullSession(): boolean {
  const cookieStr = document.cookie
  // Check for JSESSIONID cookie (case-insensitive, might have different casing)
  const hasJSessionId = /JSESSIONID\s*=/i.test(cookieStr) || /jsessionid\s*=/i.test(cookieStr)

  // Also check for CSRF-TOKEN as an indicator of active session
  const hasCsrfToken = /CSRF-TOKEN\s*=/i.test(cookieStr)

  return hasJSessionId || hasCsrfToken
}

/**
 * Проверяет, есть ли у пользователя какие-либо аутентификационные данные
 * (токен в localStorage или cookies: X-AUTH-TOKEN, JSESSIONID)
 */
export function hasAuthToken(): boolean {
  // Check localStorage
  if (localStorage.getItem('auth_token')) {
    return true
  }

  // Check cookies
  const cookieStr = document.cookie
  return cookieStr.includes('X-AUTH-TOKEN=') ||
    cookieStr.includes('JSESSIONID=')
}

/**
 * Получает токен из доступных источников (localStorage или cookies)
 */
export function getAuthToken(): string | null {
  // First check localStorage
  const token = localStorage.getItem('auth_token')
  if (token) {
    return token
  }

  // Then check cookies
  const cookieStr = document.cookie
  const authTokenMatch = cookieStr.match(/X-AUTH-TOKEN=([^;]+)/)
  if (authTokenMatch) {
    return authTokenMatch[1]
  }

  return null
}

/**
 * Очищает все аутентификационные cookies и localStorage
 * Используется при logout, session expired и на старте приложения
 */
export function clearAllCookies() {
  const cookieNames = ['X-AUTH-TOKEN', 'CSRF-TOKEN', 'JSESSIONID', 'remember-me']
  const domain = window.location.hostname
  const pastDate = 'Thu, 01 Jan 1970 00:00:00 GMT'

  // Clear all existing cookies by parsing document.cookie first
  const allCookies = document.cookie.split(';')
  allCookies.forEach(cookie => {
    const eqPos = cookie.indexOf('=')
    const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim()

    // Clear auth-related cookies found in document.cookie
    if (cookieNames.some(cn => cn.toLowerCase() === name.toLowerCase())) {
      // Try all possible variations (case-insensitive matching)
      const variations = [
        `${name}=; expires=${pastDate}; path=/`,
        `${name}=; expires=${pastDate}; path=/workflow-web-services`,
        `${name}=; expires=${pastDate}; path=/; domain=${domain}`,
        `${name}=; expires=${pastDate}; path=/; domain=.${domain}`,
        `${name}=; expires=${pastDate}; path=/workflow-web-services; domain=${domain}`,
        `${name}=; expires=${pastDate}; path=/workflow-web-services; domain=.${domain}`,
        `${name}=; expires=${pastDate}`,
        // Try with original case
        ...(name !== name.toUpperCase() ? [
          `${name.toUpperCase()}=; expires=${pastDate}; path=/`,
          `${name.toUpperCase()}=; expires=${pastDate}; path=/workflow-web-services`,
        ] : []),
        ...(name !== name.toLowerCase() ? [
          `${name.toLowerCase()}=; expires=${pastDate}; path=/`,
          `${name.toLowerCase()}=; expires=${pastDate}; path=/workflow-web-services`,
        ] : []),
      ]

      variations.forEach(variation => {
        document.cookie = variation
      })
    }
  })

  // Also clear using predefined names with all variations (case-insensitive)
  cookieNames.forEach(cookieName => {
    const variations = [
      cookieName,
      cookieName.toUpperCase(),
      cookieName.toLowerCase(),
    ]

    variations.forEach(name => {
      const paths = ['/', '/workflow-web-services']
      const domains = [domain, `.${domain}`, '']

      paths.forEach(path => {
        domains.forEach(dom => {
          const domainPart = dom ? `; domain=${dom}` : ''
          document.cookie = `${name}=; expires=${pastDate}; path=${path}${domainPart}`
        })
        document.cookie = `${name}=; expires=${pastDate}; path=${path}`
      })
      document.cookie = `${name}=; expires=${pastDate}`

      // Also use universal-cookie library to ensure cleanup
      try {
        cookies.remove(name, { path: '/' })
        cookies.remove(name, { path: '/workflow-web-services' })
        cookies.remove(name, { path: '/', domain: domain })
        cookies.remove(name, { path: '/', domain: `.${domain}` })
      } catch (e) {
        // Ignore errors from cookie removal
      }
    })
  })

  localStorage.removeItem('auth_token')

  // Force clear by reading current cookies and clearing any that match
  setTimeout(() => {
    const remainingCookies = document.cookie.split(';')
    remainingCookies.forEach(cookie => {
      const eqPos = cookie.indexOf('=')
      const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim()
      if (cookieNames.some(cn => cn.toLowerCase() === name.toLowerCase())) {
        document.cookie = `${name}=; expires=${pastDate}; path=/; max-age=0`
        document.cookie = `${name}=; expires=${pastDate}; path=/workflow-web-services; max-age=0`
      }
    })
  }, 0)
}

/**
 * Clears auth state and redirects to signin. Use when session expired or WebSocket
 * requires full session (e.g. after long inactivity with only remember-me).
 */
export function handleSessionExpired(_error?: any) {
  const currentPath = window.location.pathname + window.location.search
  localStorage.setItem('returnPath', currentPath)

  clearAllCookies()
  skipNextPageLeaveConfirm()

  window.location.href = new URL(
    'signin',
    `${window.location.origin}${import.meta.env.BASE_URL || '/'}`
  ).href
}

function isConnectionError(error: any): boolean {
  if (!error.response) return false

  const { status, data, headers } = error.response
  const url = error.config?.url || ''

  if (AUTH_ENDPOINTS.some(endpoint => url.includes(endpoint))) {
    return false
  }

  return status === 400 && (
    headers['x-connection-error'] === 'true' ||
    data?.errorType === 'CONNECTION_ERROR' ||
    (data && (
      data.message?.includes('connection') ||
      data.message?.includes('Connection') ||
      data.error?.includes('connection') ||
      data.error?.includes('Connection')
    ))
  )
}

async function handleConnectionError(error: any) {
  if (isConnectionError(error)) {
    try {
      return axiosInstance(error.config)
    } catch (retryError) {
      window.location.reload()
      return Promise.reject(retryError)
    }
  }
  return Promise.reject(error)
}

/**
 * Проверяет envelope ошибки middle (ExceptionClientModel) в успешном HTTP-ответе.
 * @see dev/contracts/middle_error_codes.md
 */
function checkForActionError(data: any): ActionError | null {
  if (isMiddleErrorResponse(data)) {
    const { code, developerMessage, message, type, url } = data
    return new ActionError(code, developerMessage ?? getMiddleErrorMessage(data), message, {
      type: typeof type === 'string' ? type : undefined,
      url: typeof url === 'string' ? url : undefined,
    })
  }

  if (data && typeof data === 'object') {
    const { code, developerMessage, message, type, url } = data

    // Legacy: code + developerMessage без url (старые ответы)
    if (code !== undefined && code !== null && developerMessage !== undefined && developerMessage !== null) {
      return new ActionError(code, developerMessage, message, {
        type: typeof type === 'string' ? type : undefined,
        url: typeof url === 'string' ? url : undefined,
      })
    }
  }
  return null
}

function isStalePlatformCoreError(data: any): boolean {
  if (!data || typeof data !== 'object') return false
  const text = JSON.stringify({
    code: data.code,
    message: data.message,
    developerMessage: data.developerMessage,
    error: data.error,
    exception: data.exception,
  }).toLowerCase()

  return (
    text.includes('core cannot be null') ||
    text.includes('middle_invalid_core') ||
    text.includes('invalid core') ||
    text.includes('core_id')
  )
}

function clearStalePlatformCoreState() {
  try {
    localStorage.removeItem('app_pagesData')
  } catch {
    // ignore localStorage errors
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('workflow:stale-platform-core'))
  }
}

/**
 * Обработчик ошибок
 */
async function catchHandler(error: any) {
  if (axios.isCancel(error)) {
    return Promise.reject(error)
  }

  const customErrorConfig = error.config as CustomAxiosRequestConfig | undefined
  if (customErrorConfig?._skipGlobalErrorHandling) {
    return Promise.reject(error)
  }

  const url = error.config?.url || ''
  const isAuthRequest = AUTH_ENDPOINTS.some(endpoint => url.includes(endpoint))

  if (error.response?.data) {
    const { status, code, message, developerMessage } = error.response.data
      ; (error as any).workflowDetails = {
        status,
        code,
        message,
        developerMessage
      }
    if (message && typeof message === 'string') {
      error.message = message
    }
    if (isStalePlatformCoreError(error.response.data)) {
      clearStalePlatformCoreState()
    }
  }

  // Обработка CSRF ошибок
  if (error.response?.status === 403) {
    const responseData = error.response?.data || {}
    const responseHeaders = error.response?.headers || {}
    const hasCsrfToken = !!cookies.get('CSRF-TOKEN')
    const isPublicEndpoint = url.includes('/user/systems') || url.includes('/user/signup') || url.includes('/version')
    const isCsrfError =
      responseData?.message?.toLowerCase().includes('csrf') ||
      responseData?.error?.toLowerCase().includes('csrf') ||
      responseHeaders?.['x-csrf-error'] === 'true' ||
      responseHeaders?.['X-CSRF-Error'] === 'true' ||
      (!isAuthRequest && !hasCsrfToken && !isPublicEndpoint)

    if (isCsrfError) {
      const retryCount = (error.config as any)?._csrfRetryCount || 0
      if (retryCount < 2 && !hasCsrfToken) {
        ; (error.config as any)._csrfRetryCount = retryCount + 1
        await new Promise(resolve => setTimeout(resolve, 100 * (retryCount + 1)))
        return axiosInstance(error.config)
      }
      return Promise.reject(error)
    } else if (isPublicEndpoint && !hasCsrfToken) {
      return Promise.reject(error)
    }
  }

  if (isSessionExpiredError(error)) {
    handleSessionExpired(error as any)
    return Promise.reject(error)
  }

  if (isAuthRequest) {
    return Promise.reject(error)
  }

  const connectionRestored = await handleConnectionError(error)
  if (connectionRestored) return connectionRestored

  return Promise.reject(error)
}

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const customConfig = config as CustomAxiosRequestConfig

    const isAuthEndpoint = AUTH_ENDPOINTS.some(endpoint =>
      config.url?.includes(endpoint)
    )

    const isSignInEndpoint = config.url?.includes('/user/signin')
    const isPublicEndpoint = config.url?.includes('/user/systems') ||
      config.url?.includes('/user/signup') ||
      config.url?.includes('/version')

    // For signin endpoint, don't send CSRF token
    if (isSignInEndpoint) {
      delete customConfig.headers['X-CSRF-TOKEN']
    } else if (!isPublicEndpoint) {
      // For protected endpoints, send CSRF token if available
      const csrfToken = cookies.get('CSRF-TOKEN')
      if (csrfToken) {
        customConfig.headers['X-CSRF-TOKEN'] = csrfToken
      }
    } else if (isPublicEndpoint) {
      // For public endpoints, try to send CSRF token if available
      const csrfToken = cookies.get('CSRF-TOKEN')
      if (csrfToken) {
        customConfig.headers['X-CSRF-TOKEN'] = csrfToken
      }
    }

    if (!isAuthEndpoint) {
      const tokenFromStorage = localStorage.getItem('auth_token')
      if (tokenFromStorage) {
        customConfig.headers['X-AUTH-TOKEN'] = tokenFromStorage
      }

      const tokenFromCookies = cookies.get('X-AUTH-TOKEN')
      if (tokenFromCookies && !customConfig.headers['X-AUTH-TOKEN']) {
        customConfig.headers['X-AUTH-TOKEN'] = tokenFromCookies
      }
    }

    if (customConfig.method?.toLowerCase() === 'get' && !isAuthEndpoint) {
      customConfig.params = {
        ...customConfig.params,
        _t: Date.now()
      }
    }

    // FormData: убрать application/json по умолчанию — браузер выставит multipart + boundary
    if (typeof FormData !== 'undefined' && customConfig.data instanceof FormData) {
      if (customConfig.headers) {
        delete customConfig.headers['Content-Type']
        delete customConfig.headers['content-type']
      }
    }

    if (customConfig.url?.includes('/user/sign')) {
      customConfig._isAuthRequest = true
    }

    if (isWorkflowPerfEnabled()) {
      customConfig._workflowRequestProfile = profilePayload(customConfig.data)
      customConfig._workflowPerfEnd = startWorkflowMeasure(
        `http ${String(customConfig.method || 'GET').toUpperCase()} ${customConfig.url || ''}`,
        {
          request: customConfig._workflowRequestProfile,
        },
      )
    }

    return customConfig
  },
  (error) => {
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    const customConfig = response.config as CustomAxiosRequestConfig
    const durationMs = customConfig._workflowPerfEnd?.()
    if (isWorkflowPerfEnabled()) {
      console.debug('[workflow:http]', {
        method: response.config?.method?.toUpperCase(),
        url: response.config?.url,
        status: response.status,
        durationMs: durationMs == null ? undefined : Math.round(durationMs * 10) / 10,
        request: customConfig._workflowRequestProfile,
        response: profilePayload(response.data),
      })
    }

    // Проверяем наличие code и developerMessage в успешном ответе
    const actionError = checkForActionError(response.data)
    if (actionError) {
      if (isStalePlatformCoreError(response.data)) {
        clearStalePlatformCoreState()
      }
      handleActionErrorGlobal(actionError)
      return Promise.reject(actionError)
    }

    // Handle logout - always clear cookies on successful logout response
    if (response.config?.url?.includes('/user/signout') && response.status === 200) {
      clearAllCookies()
    }

    if ((response.config?.url?.includes('/user/signin') || response.config?.url?.includes('/user/signinByCard')) && response.status === 200) {
      const setCookieHeaders = response.headers['set-cookie'] || response.headers['Set-Cookie'] || []
      if (Array.isArray(setCookieHeaders)) {
        setCookieHeaders.forEach((cookieHeader: string) => {
          // Parse cookie attributes
          const parts = cookieHeader.split(';')
          const [nameValue] = parts
          const [name, value] = nameValue.split('=')

          if (name && value && ['CSRF-TOKEN', 'X-AUTH-TOKEN'].includes(name.trim())) {
            // Extract path, domain, maxAge, etc. from the cookie header
            let cookieString = `${name.trim()}=${value.trim()}`

            // Try to preserve path attribute
            const pathMatch = cookieHeader.match(/path=([^;]+)/i)
            const path = pathMatch ? pathMatch[1].trim() : '/'
            cookieString += `; path=${path}`

            // Try to preserve domain if present
            const domainMatch = cookieHeader.match(/domain=([^;]+)/i)
            if (domainMatch) {
              cookieString += `; domain=${domainMatch[1].trim()}`
            }

            // Preserve maxAge/expires if present
            const maxAgeMatch = cookieHeader.match(/max-age=([^;]+)/i)
            if (maxAgeMatch) {
              cookieString += `; max-age=${maxAgeMatch[1].trim()}`
            } else {
              const expiresMatch = cookieHeader.match(/expires=([^;]+)/i)
              if (expiresMatch) {
                cookieString += `; expires=${expiresMatch[1].trim()}`
              }
            }

            cookieString += '; SameSite=Lax'

            document.cookie = cookieString
          }
        })
      }
    }

    // Token is set via cookie by backend (rememberMeServices), not in response body
    if (response.data?.token && !response.config?.url?.includes('/user/signin')) {
      localStorage.setItem('auth_token', response.data.token)
      cookies.set('X-AUTH-TOKEN', response.data.token, {
        secure: false,
        sameSite: 'lax'
      })
    }

    return response
  },
  async (error) => {
    const customConfig = error.config as CustomAxiosRequestConfig | undefined
    customConfig?._workflowPerfEnd?.()
    return catchHandler(error)
  }
)

export default {
  install(app: any) {
    app.config.globalProperties.$axios = axiosInstance
    app.config.globalProperties.$http = axiosInstance

    app.provide('axios', axiosInstance)

      ; (window as any).axios = axiosInstance
  }
}

export { axiosInstance as axios }
